/**
 * The places the business works from: head office and each clinic.
 *
 * A location carries the GSTIN, because GST registration is per state, not
 * per company — which is why the compliance calendar counts returns from here.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type Location } from '../api'
import { useAuth } from '../auth'
import { ErrorNote, Field, Loading, Note, Stat } from '../components/ui'
import { Pager, usePaged } from '../components/Pager'
import { SidePanel, Tabs, matches } from '../components/SidePanel'

const KINDS = ['clinic', 'corporate', 'warehouse', 'virtual']

type Tab = 'active' | 'inactive' | 'all'

export default function Locations() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const editable = can('org.location.configure')
  const [adding, setAdding] = useState(false)
  const [tab, setTab] = useState<Tab>('active')
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<Location[]>('/org/locations'),
  })

  const [form, setForm] = useState({
    code: '',
    name: '',
    kind: 'clinic',
    state_code: '',
    city: '',
    gstin: '',
  })

  const create = useMutation({
    mutationFn: () =>
      api.post<Location>('/org/locations', {
        ...form,
        gstin: form.gstin || null,
        state_code: form.state_code || null,
        city: form.city || null,
      }),
    onSuccess: () => {
      setForm({ code: '', name: '', kind: 'clinic', state_code: '', city: '', gstin: '' })
      setAdding(false)
      void queryClient.invalidateQueries({ queryKey: ['locations'] })
    },
  })

  const rows = data ?? []
  const shown = rows.filter(
    (r) =>
      (tab === 'all' || (tab === 'active' ? r.is_active : !r.is_active)) &&
      (!kind || r.kind === kind) &&
      matches(search, r.code, r.name, r.city, r.gstin, r.state_code),
  )
  const paged = usePaged(shown, `${tab}|${kind}|${search}`)

  if (isLoading) return <Loading what="Loading locations" />

  const active = rows.filter((r) => r.is_active)
  const states = new Set(rows.map((r) => r.state_code).filter(Boolean))
  const gstins = new Set(active.map((r) => r.gstin).filter(Boolean))
  const kinds = Array.from(new Set(rows.map((r) => r.kind))).sort()

  return (
    <>
      <ErrorNote error={error} />

      {editable && (
        <div className="bank-toolbar">
          <button className="primary" onClick={() => setAdding(true)}>
            Add a location
          </button>
        </div>
      )}

      {states.size > 1 && (
        <Note kind="warn">
          Locations span {states.size} states ({[...states].join(', ')}). GST registration is per
          state, so the group holds more than one GSTIN — separate returns per registration, and
          transfers between branches become taxable supplies. That is not yet built.
        </Note>
      )}

      <div className="grid">
        <Stat
          label="Active locations"
          value={String(active.length)}
          note={`${rows.length - active.length} inactive`}
        />
        <Stat
          label="Clinics"
          value={String(active.filter((r) => r.kind === 'clinic').length)}
          note="where the takings come from"
        />
        <Stat
          label="GST registrations"
          value={String(gstins.size)}
          note="one per state the business is registered in"
        />
        <Stat
          label="States"
          value={String(states.size)}
          note={states.size ? [...states].join(', ') : 'no state codes recorded'}
        />
      </div>

      <section className="card wide">
        <header className="appr-toolbar">
          <Tabs
            tabs={[
              { key: 'active', label: 'Active', count: active.length },
              {
                key: 'inactive',
                label: 'Inactive',
                count: rows.length - active.length,
                quietWhenZero: true,
              },
              { key: 'all', label: 'All', count: rows.length },
            ]}
            value={tab}
            onChange={setTab}
          />
          <div className="appr-filters">
            <input
              type="search"
              placeholder="Code, name, city, GSTIN"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search locations"
            />
            <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind">
              <option value="">Every kind</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {k.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
        </header>
        <div className="body tight">
          {shown.length === 0 ? (
            <div className="empty">
              {rows.length === 0
                ? 'No locations yet. Add the head office and each clinic.'
                : 'No location matches that.'}
            </div>
          ) : (
            <table className="appr-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Kind</th>
                  <th>City</th>
                  <th>State</th>
                  <th>GSTIN</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="strong">{r.name}</div>
                      <div className="cell-sub ref">{r.code}</div>
                    </td>
                    <td className="muted">{r.kind.replace(/_/g, ' ')}</td>
                    <td>{r.city ?? <span className="faint">—</span>}</td>
                    <td className="mono">{r.state_code ?? <span className="faint">—</span>}</td>
                    <td className="mono">{r.gstin ?? <span className="faint">—</span>}</td>
                    <td>
                      <span className={`pill ${r.is_active ? 'approved' : 'draft'}`}>
                        {r.is_active ? 'active' : 'inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} />
      </section>

      {adding && editable && (
        <SidePanel
          kicker="Location"
          title="Add a location"
          sub="the head office, a clinic, a warehouse"
          onClose={() => setAdding(false)}
        >
          <ErrorNote error={create.error} />
          <form
            onSubmit={(e) => {
              e.preventDefault()
              create.mutate()
            }}
          >
            <div className="row">
              <Field label="Code">
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  required
                  maxLength={20}
                />
              </Field>
              <Field label="Kind">
                <select
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                >
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Name">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Field>
            <div className="row">
              <Field label="City">
                <input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </Field>
              <Field label="State code" caption="two digits, e.g. 27 for Maharashtra">
                <input
                  value={form.state_code}
                  onChange={(e) => setForm({ ...form, state_code: e.target.value })}
                  maxLength={2}
                />
              </Field>
            </div>
            <Field label="GSTIN" caption="per state, not per company">
              <input
                value={form.gstin}
                onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                maxLength={15}
              />
            </Field>
            <div className="row" style={{ marginTop: 12 }}>
              <div className="shrink">
                <button className="primary" type="submit" disabled={create.isPending}>
                  {create.isPending ? 'Adding…' : 'Add location'}
                </button>
              </div>
            </div>
          </form>
        </SidePanel>
      )}
    </>
  )
}
